// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import PieChart from "examples/Charts/PieChart";

import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";
import AppConfig from "appConfig";
import { useAuthUser } from "react-auth-kit";

function AgentStatsPage(payload) {
  const { campaignReference } = payload;
  const auth = useAuthUser();
  const accountInfo = auth();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [performances, setPerformances] = useState([]);
  const [currency, setCurrency] = useState(null);
  const [campaign, setCampaign] = useState({});
  const [activeAgents, setActiveAgents] = useState(0);
  const [totalAgents, setTotalAgents] = useState(0);
  const [groupMetrics, setGroupMetrics] = useState(null);
  const [pageLoaded, setPageLoaded] = useState(false);

  const tableColumns = [
    { Header: "Performance", accessor: "arrow", align: "left" },
    { Header: "Agent", accessor: "agent", align: "left" },
    { Header: "Transactions", accessor: "sales", align: "center" },
    // { Header: "Vouchers", accessor: "vouchers", align: "center" },
    // { Header: "Performance", accessor: "perf", align: "center" },
  ];

  function buildRateRows(data, average) {
    const rows = [];
    data.map((row) => {
      rows.push({
        arrow: (
          <Icon color={average > row.sales ? "error" : "success"} fontSize="small">
            {average > row.sales ? "south" : "north"}
          </Icon>
        ),
        agent: row.agent,
        sales: row.sales,
        // vouchers: row.vouchers,
      });
      return null;
    });

    return rows;
  }

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`/portal/campaigns/${campaignReference}/agent/performance.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setCampaign(result.data.data.campaign);
            setCurrency(result.data.data.currency);
            setActiveAgents(result.data.data.activeAgents);
            setTotalAgents(result.data.data.totalAgents);
            setGroupMetrics(result.data.data.groupMetrics);
            const rows = buildRateRows(result.data.data.performance, result.data.data.average);
            setPerformances(rows);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  function getPieChartColors(data) {
    const colors = ["info", "primary", "secondary", "error", "warning", "success"];
    const bgColors = [];
    data.every(() => {
      bgColors.push(colors[Math.floor(Math.random() * colors.length)]);
      return true;
    });

    return bgColors;
  }

  function getPieChartLabels(data) {
    const labels = [];
    data.every((row) => {
      labels.push(row.label);
      return true;
    });

    return labels;
  }

  function getPieChartData(data) {
    const datas = [];
    data.every((row) => {
      datas.push(row.count);
      return true;
    });

    return datas;
  }

  return (
    <MDBox>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {pageLoaded && (
        <MDBox pt={3} pb={3}>
          {groupMetrics && (
            <MDBox mt={4} mb={5}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4} lg={4}>
                  <PieChart
                    icon={{ color: "info", component: "person_alt" }}
                    title="Team Insights"
                    description="Performance per team"
                    chart={{
                      labels: getPieChartLabels(groupMetrics),
                      datasets: {
                        label: "Team Insights",
                        backgroundColors: getPieChartColors(groupMetrics),
                        data: getPieChartData(groupMetrics),
                      },
                    }}
                    height={180}
                  />
                </Grid>
                <Grid item xs={12} md={8} lg={8}>
                  {campaign.campaign_type === "SALES" ? (
                    <Card sx={{ height: "100%" }}>
                      <MDBox p={2}>
                        <Table sx={{ width: "100%" }} aria-label="simple table">
                          <TableBody>
                            <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                              <TableCell>
                                <MDTypography variant="subtitle2" fontSize={14} fontWeight="bold">
                                  Team
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography variant="subtitle2" fontSize={14} fontWeight="bold">
                                  Count
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography variant="subtitle2" fontSize={14} fontWeight="bold">
                                  Volume
                                </MDTypography>
                              </TableCell>
                            </TableRow>
                            {groupMetrics.map((team) => (
                              <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                <TableCell>
                                  <MDTypography variant="subtitle2" fontSize={14}>
                                    {team.label}
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDTypography variant="subtitle2" fontSize={14}>
                                    {team.count.toLocaleString("en", {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    })}
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDTypography variant="subtitle2" fontSize={14}>{`${currency}${(
                                    team.volume / 100
                                  ).toLocaleString("en", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}`}</MDTypography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </MDBox>
                    </Card>
                  ) : (
                    <Card sx={{ height: "100%" }}>
                      <MDBox p={2}>
                        <Table sx={{ width: "100%" }} aria-label="simple table">
                          <TableBody>
                            <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                              <TableCell>
                                <MDTypography variant="subtitle2" fontSize={14} fontWeight="bold">
                                  Team
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography variant="subtitle2" fontSize={14} fontWeight="bold">
                                  Count
                                </MDTypography>
                              </TableCell>
                            </TableRow>
                            {groupMetrics.map((team) => (
                              <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                <TableCell>
                                  <MDTypography variant="subtitle2" fontSize={14}>
                                    {team.label}
                                  </MDTypography>
                                </TableCell>
                                <TableCell>
                                  <MDTypography variant="subtitle2" fontSize={14}>
                                    {team.count.toLocaleString("en", {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 0,
                                    })}
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </MDBox>
                    </Card>
                  )}
                </Grid>
              </Grid>
            </MDBox>
          )}
          <MDBox>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={3}>
                <MDBox p={0}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4} lg={4}>
                      <MDBox p={1}>
                        <MDTypography variant="h6" fontWeight="medium">
                          Performance
                        </MDTypography>
                        <MDTypography color="info" variant="caption" fontWeight="medium">
                          Active: {activeAgents} / {totalAgents}
                        </MDTypography>
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} md={8} lg={8}>
                      <MDBox align="right">
                        <MDBox variant="button">
                          <MDButton
                            variant="outlined"
                            color="dark"
                            size="small"
                            onClick={() => {
                              const downloadUrl = `${AppConfig.api_base_url}campaigns/reports/agents/${campaignReference}/${accountInfo.user.reference}/export.json`;
                              window.open(downloadUrl, "_blank");
                            }}
                            startIcon={<Icon sx={{ fontWeight: "bold" }}>download</Icon>}
                          >
                            Report
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </Grid>
                  </Grid>
                </MDBox>
                <MDBox mt={3}>
                  <DataTable
                    table={{ columns: tableColumns, rows: performances }}
                    isSorted
                    entriesPerPage={false}
                    showTotalEntries={false}
                    buttons
                    noEndBorder
                  />
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        </MDBox>
      )}
    </MDBox>
  );
}

export default AgentStatsPage;
